import { useEffect, useState } from 'react';
import { RawIntlProvider } from 'react-intl';

import { createIntlInstance, getIntl } from '@avalara/bat-apps-shared/i18n';

export interface IntlProviderWrapperProps {
  children: React.ReactNode;
}

interface SLocaleSelectCustomEvent extends CustomEvent {
  detail: {
    locale?: string;
  };
}

const IntlProviderWrapper = ({ children }: IntlProviderWrapperProps) => {
  const [currentLocale, setCurrentLocale] = useState(() => {
    return window.AvaAppUser?.locale || 'en-US';
  });
  const [intl, setIntl] = useState(getIntl());

  // Listen for locale change events (Skylab header/footer)
  useEffect(() => {
    const handleLocaleSelect = (event: SLocaleSelectCustomEvent) => {
      const newLocale = event.detail?.locale;
      if (newLocale && newLocale !== currentLocale) setCurrentLocale(newLocale);
    };

    window.addEventListener('s-localeselect', handleLocaleSelect as EventListener);
    return () => {
      window.removeEventListener('s-localeselect', handleLocaleSelect as EventListener);
    };
  }, [currentLocale]);

  // Load translations when locale changes
  useEffect(() => {
    const loadAndUpdateTranslations = async () => {
      try {
        await createIntlInstance(currentLocale);
        setIntl(getIntl());
      } catch (error) {
        console.warn('Failed to load translations:', error);
      }
    };

    loadAndUpdateTranslations();
  }, [currentLocale]);

  return (
    <RawIntlProvider value={intl} key={currentLocale}>
      {children}
    </RawIntlProvider>
  );
};

export default IntlProviderWrapper;
